import i18next from 'i18next';
import { hideNotification, showNotification, updateNotification } from '@mantine/notifications';
import { AxiosError } from 'axios';
import { IconAlertCircle } from '@tabler/icons-react';
import { NOTIFICATION_AUTO_CLOSE_TIME } from '@/constants';
import { handleForbiddenRedirectError } from './forbiddenRedirect';
import { isChunkLoadError, notifyAppVersionUpdate } from './appVersion';
import type { ApiForbiddenErrorBody } from '@/types/apiError';

type ApiValidationError = {
  property?: string;
  field?: string;
  messages?: unknown[];
  message?: unknown;
};

type ApiErrorResponse = ApiForbiddenErrorBody & {
  detail?: string;
  errors?: ApiValidationError[];
};

export type ApiValidationIssue = {
  property: string;
  messages: string[];
};

const isAxiosError = (error: unknown): error is AxiosError<ApiErrorResponse> => error instanceof AxiosError;

export { isChunkLoadError, importWithRetry } from './appVersion';

const normalizeMessages = (messages?: unknown[]): string[] => {
  if (!Array.isArray(messages)) return [];

  return Array.from(
    new Set(
      messages
        .map((m) => String(m).trim())
        .filter(Boolean)
        .map((m) => m.replace(/\.*$/, '')) // remove trailing dots
    )
  );
};

const VALIDATION_FIELD_LABEL_KEYS: Record<string, string> = {
  phone: 'form.phoneNumber',
  phonenumber: 'form.phoneNumber',
  email: 'form.email',
  firstname: 'form.firstName',
  lastname: 'form.lastName',
  middlename: 'form.middleName',
  dob: 'form.birthday',
  birthday: 'form.birthday',
  phonepersonal: 'form.phonePersonal',
  phoneoffice: 'form.phoneOffice',
  phoneofficeextension: 'form.phoneOfficeExtension',
};

const COMPARE_UPDATE_PROPERTY_PATTERN = /^update\[(\d+)\]/;

const resolveValidationField = (error: ApiValidationError): string => String(error.property ?? error.field ?? '');

const resolveValidationMessages = (error: ApiValidationError): string[] => {
  if (Array.isArray(error.messages) && error.messages.length > 0) {
    return normalizeMessages(error.messages);
  }

  if (error.message !== undefined && error.message !== null) {
    return normalizeMessages([error.message]);
  }

  return [];
};

const getValidationPropertyLeaf = ({ property }: { property: string }): string => {
  const trimmedProperty = property.trim();
  if (!trimmedProperty) {
    return '';
  }

  const segments = trimmedProperty.split('.').filter(Boolean);
  const lastSegment = segments[segments.length - 1] || trimmedProperty;

  return lastSegment.replace(/\[\d+\]/g, '');
};

const getValidationFieldLabel = ({ property }: { property: string }): string => {
  const leaf = getValidationPropertyLeaf({ property });
  if (!leaf) {
    return '';
  }

  const labelKey = VALIDATION_FIELD_LABEL_KEYS[leaf.toLowerCase()];
  if (!labelKey) {
    return '';
  }

  return String(i18next.t(labelKey, { ns: 'global' }));
};

const getCompareMemberFieldPrefix = ({ property }: { property: string }): string => {
  const match = property.trim().match(COMPARE_UPDATE_PROPERTY_PATTERN);
  if (!match) {
    return '';
  }

  const memberNumber = Number(match[1]) + 1;

  return String(i18next.t('members.compareModal.memberLabel', { ns: 'global', number: memberNumber }));
};

const formatValidationFieldPrefix = ({ property }: { property: string }): string => {
  const leaf = getValidationPropertyLeaf({ property });
  const fieldLabel = getValidationFieldLabel({ property });

  if (!fieldLabel || leaf.toLowerCase() === 'id') {
    return '';
  }

  const memberPrefix = getCompareMemberFieldPrefix({ property });
  const prefix = memberPrefix ? `${memberPrefix} ${fieldLabel}` : fieldLabel;

  return `${prefix}: `;
};

const formatValidationErrors = (errors: ApiValidationError[]): string | null => {
  const lines = errors
    .map((error) => {
      const uniqueMessages = resolveValidationMessages(error);
      if (uniqueMessages.length === 0) return null;

      const message = `${uniqueMessages.join('. ')}.`;
      const field = resolveValidationField(error);
      const fieldPrefix = formatValidationFieldPrefix({ property: field });

      return `${fieldPrefix}${message}`;
    })
    .filter(Boolean) as string[];

  return lines.length > 0 ? lines.join('\n') : null;
};

export const getApiValidationIssues = (error: unknown): ApiValidationIssue[] => {
  if (!isAxiosError(error)) return [];

  const errors = error.response?.data?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return [];

  return errors
    .map((e) => ({
      property: resolveValidationField(e),
      messages: resolveValidationMessages(e),
    }))
    .filter((e) => Boolean(e.property) || e.messages.length > 0);
};

const stripPropertyPrefixes = (text: string): string => text.replace(/^\s*\[[^[\]]*(?:\[[^[\]]*\][^[\]]*)*\]\s*/gm, '');

export const isRequestCancelledError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as { code?: string; name?: string; message?: string };

  if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError') {
    return true;
  }

  // Axios XHR abort on unmount / test teardown uses this message with ECONNABORTED (also used for timeouts).
  return err.message === 'Request aborted';
};

export const composeErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const data = error.response?.data;

    // Validation errors
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      const formatted = formatValidationErrors(data.errors);
      if (formatted) return stripPropertyPrefixes(formatted);
    }

    // Generic API message
    const apiMessage = data?.message || data?.detail;
    return stripPropertyPrefixes(apiMessage ? String(apiMessage) : error.message);
  }

  if (error instanceof Error) {
    return stripPropertyPrefixes(error.message);
  }

  return stripPropertyPrefixes(String(error));
};

export const handleError = (error: unknown, notificationId?: string): void => {
  if (handleForbiddenRedirectError(error)) {
    return;
  }

  if (isChunkLoadError(error)) {
    notifyAppVersionUpdate();
    return;
  }

  // Session-end 401s redirect to login in the API interceptor; do not surface
  // a technical "Unable to validate access token" (or similar) toast.
  // Impersonation 401s already show their own dedicated notification there.
  if (isAxiosError(error) && error.response?.status === 401) {
    if (notificationId?.trim()) {
      hideNotification(notificationId);
    }
    return;
  }

  const notification = {
    title: i18next.t('error', { ns: 'global' }),
    message: composeErrorMessage(error),
    color: 'red',
    icon: <IconAlertCircle />,
    autoClose: NOTIFICATION_AUTO_CLOSE_TIME,
    withCloseButton: true,
    loading: false,
  };

  if (notificationId?.trim()) {
    updateNotification({ id: notificationId, ...notification });
    return;
  }

  showNotification(notification);
};
